import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { cropApi } from '@/services/cropApi';

export default function LoginPage() {
  const [deviceInput, setDeviceInput] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    // Convert to uppercase first, then strip non-hex characters
    const val = raw.toUpperCase();
    const hexOnly = val.replace(/[^0-9A-F]/g, '');
    setDeviceInput(hexOnly);
    if (error) setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = deviceInput.trim();
    if (trimmed.length < 4) {
      setError('최소 4자리 이상 입력해주세요.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await cropApi.login(trimmed);
      if (res.result === 'success' && res.device_id) {
        localStorage.setItem('device_id', res.device_id);
        navigate('/dashboard');
      } else {
        setError(res.message || '해당 번호의 장비를 찾을 수 없습니다.');
      }
    } catch (err: unknown) {
      if (err instanceof Error && err.message.includes('HTTP error')) {
        setError(`서버 응답 오류입니다. 장비 번호를 다시 확인해주세요.`);
      } else {
        setError('서버에 연결할 수 없습니다. 네트워크를 확인해주세요.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-4"
      style={{
        background: 'linear-gradient(135deg, #0f2d1a 0%, #1B4F72 60%, #145a32 100%)',
      }}
    >
      {/* Background pattern */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-full opacity-5">
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className="absolute text-white text-8xl select-none"
              style={{
                top: `${10 + i * 12}%`,
                left: `${(i * 13) % 90}%`,
                transform: `rotate(${i * 15}deg)`,
                fontSize: '6rem',
              }}
            >
              🌾
            </div>
          ))}
        </div>
      </div>

      <div className="relative z-10 w-full max-w-md">
        {/* Logo & Title */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="w-20 h-20 flex items-center justify-center rounded-2xl overflow-hidden bg-white/10 backdrop-blur-sm border border-white/20">
              <img
                src="https://readdy.ai/api/search-image?query=rice%20paddy%20field%20aerial%20view%20green%20lush%20healthy%20crop%20top%20down%20shot%20bright%20daylight%20clear%20sky%20simple%20background&width=80&height=80&seq=logo1&orientation=squarish"
                alt="Logo"
                className="w-16 h-16 object-cover object-top"
              />
            </div>
          </div>
          <h1
            className="text-3xl font-bold text-white tracking-tight"
            style={{ fontFamily: '\'Malgun Gothic\', \'맑은 고딕\', sans-serif' }}
          >
            벼 작황 모니터링
          </h1>
          <p className="text-green-300 mt-1 text-sm">Rice Crop Monitoring System</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Card Header */}
          <div className="px-8 py-5" style={{ background: 'linear-gradient(90deg, #1B4F72, #27ae60)' }}>
            <div className="flex items-center gap-2 text-white">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-login-box-line text-lg"></i>
              </div>
              <span className="font-semibold text-base">장치 조회</span>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="px-8 py-8">
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-600 mb-2">
                장비 번호
              </label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400">
                  <i className="ri-cpu-line text-lg"></i>
                </div>
                <input
                  type="text"
                  value={deviceInput}
                  onChange={handleInputChange}
                  placeholder="예: 3C7C 또는 3C6BC3AA3C7C"
                  className="w-full pl-10 pr-4 py-3.5 text-base font-mono border-2 border-gray-200 rounded-xl focus:outline-none focus:border-green-500 transition-colors"
                  style={{
                    fontFamily: '\'Courier New\', monospace',
                    letterSpacing: '0.12em',
                  }}
                  disabled={loading}
                  autoComplete="off"
                  autoCapitalize="characters"
                  spellCheck={false}
                />
              </div>
              {error && (
                <p className="mt-2 text-xs text-red-500 flex items-center gap-1">
                  <i className="ri-error-warning-line"></i>
                  {error}
                </p>
              )}
              <p className="mt-3 text-xs text-gray-400 leading-relaxed">
                장비 번호(MAC) 전체 또는 끝 4자리를 입력하세요.<br />
                <span className="text-gray-300">영문자(A-F)와 숫자(0-9)로 구성된 16진수입니다.</span>
              </p>
            </div>

            <button
              type="submit"
              disabled={loading || deviceInput.length < 4}
              className="w-full py-3.5 rounded-xl text-white font-bold text-base cursor-pointer whitespace-nowrap transition-all hover:opacity-90 active:scale-95 disabled:opacity-60"
              style={{ background: 'linear-gradient(90deg, #1B4F72, #27ae60)' }}
            >
              <span className="flex items-center justify-center gap-2">
                {loading ? (
                  <>
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-loader-4-line animate-spin"></i>
                    </div>
                    확인 중...
                  </>
                ) : (
                  <>
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-search-line"></i>
                    </div>
                    조회하기
                  </>
                )}
              </span>
            </button>
          </form>

          {/* Footer */}
          <div className="px-8 pb-6 text-center">
            <p className="text-xs text-gray-400">
              © 2026 Rice Crop Monitoring System. All rights reserved.
            </p>
          </div>
        </div>

        {/* Info cards */}
        <div className="mt-6 grid grid-cols-3 gap-3">
          {[
            { icon: 'ri-image-2-line', label: 'RGB / NIR / ToF', sub: '4채널 이미지' },
            { icon: 'ri-leaf-line', label: 'NDVI 분석', sub: '식생 지수 추이' },
            { icon: 'ri-bar-chart-line', label: '히스토리', sub: '촬영 이력 조회' },
          ].map((item, i) => (
            <div
              key={i}
              className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-center border border-white/20"
            >
              <div className="w-8 h-8 flex items-center justify-center mx-auto mb-1">
                <i className={`${item.icon} text-green-300 text-xl`}></i>
              </div>
              <p className="text-white text-xs font-semibold">{item.label}</p>
              <p className="text-green-300 text-xs mt-0.5">{item.sub}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}